import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type AppRole = "super_admin" | "partner_admin" | "member" | "guest";

export type UserRoleRow = {
  id: string;
  role: AppRole;
  partner_org_id: string | null;
  expires_at: string | null;
};

export function useRoles() {
  const { user, loading: authLoading } = useAuth();
  const query = useQuery({
    enabled: !!user,
    queryKey: ["user-roles", user?.id],
    queryFn: async (): Promise<UserRoleRow[]> => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("id, role, partner_org_id, expires_at")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []) as UserRoleRow[];
    },
  });

  const roles = query.data ?? [];
  const now = Date.now();
  const isActive = (r: UserRoleRow) =>
    !r.expires_at || new Date(r.expires_at).getTime() > now;
  const activeRoles = roles.filter(isActive);
  const partnerOrgIds = roles
    .filter(isActive)
    .filter((r) => r.role === "partner_admin" && r.partner_org_id)
    .map((r) => r.partner_org_id as string);

  return {
    loading: authLoading || query.isLoading,
    roles,
    isSuperAdmin: activeRoles.some((r) => r.role === "super_admin"),
    isGuest: activeRoles.some((r) => r.role === "guest"),
    isMember: activeRoles.some((r) => r.role === "member"),
    isGuestExpired:
      roles.some((r) => r.role === "guest") &&
      !activeRoles.some((r) => r.role === "guest"),
    isPartnerAdmin: partnerOrgIds.length > 0,
    partnerOrgIds,
  };
}